import React, { useState, useRef, useEffect } from 'react'
import { isEmpty } from 'lodash'
import Markdown from 'marked-react'
import { IconCheck, IconX } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Loader } from '@/components/loaders/Loader'
import { TextArea } from '@/components/forms/TextArea'
import { kodiMarkdownRenderer } from '../kodiMarkdown'

const OTHER_ROW = { isOther: true, label: 'Something else…' }

// Kodi deep-links entities with markdown, but a choice button isn't a link —
// show the link text, not raw `[text](url)`.
const stripLinks = (text) => text?.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')

/**
 * Clarifying-question panel docked above the composer (the "interactive window").
 * Options are a vertical, numbered list — click a row or press its number (1–9) to
 * pick. Single-select sends immediately; multi-select toggles and confirms with Enter.
 * Each choice goes back as an ordinary prompt.
 *
 * @param {string} props.question - the question shown to the user
 * @param {{label: string, description?: string}[]} props.options - suggested answers
 * @param {boolean} props.multiSelect - allow choosing more than one option
 * @param {(message: string) => void} props.onRespond - send the user's reply as a prompt
 * @param {() => void} props.onClose - dismiss the window and end the flow
 * @param {boolean} props.disabled - disable while a turn is in flight or disconnected
 * @param {boolean} [props.allowOther] - offer a free-text "Something else…" row
 * @param {string} [props.loadingLabel] - option whose reply is in flight; locked, with a spinner in its check slot
 */
export const KodiInteractivePrompt = ({
  question,
  options = [],
  multiSelect = false,
  onRespond,
  onClose,
  disabled = false,
  allowOther = true,
  loadingLabel,
}) => {
  const [selected, setSelected] = useState([])
  const [writingOther, setWritingOther] = useState(false)
  const [otherText, setOtherText] = useState('')
  const containerRef = useRef(null)

  // Focus the panel when it appears so number-key selection works without a
  // click, and doesn't hijack digits typed in the composer.
  useEffect(() => {
    containerRef.current?.focus()
  }, [])
  // A clicked row that locks loses focus to the body as it disables; keep the
  // panel focused so Escape still dismisses mid-flight.
  useEffect(() => {
    if (loadingLabel) {
      containerRef.current?.focus()
    }
  }, [loadingLabel])

  const trimmedOther = otherText.trim()
  const rows = allowOther ? [...options, OTHER_ROW] : options

  const toggle = (label) => {
    setSelected((current) =>
      current.includes(label) ? current.filter((item) => item !== label) : [...current, label]
    )
  }

  const activateRow = (row) => {
    // The in-flight row is locked for the keyboard too, not just the disabled button.
    if (disabled || (!row.isOther && row.label === loadingLabel)) {
      return
    }
    if (row.isOther) {
      setWritingOther(true)
      return
    }
    if (multiSelect) {
      toggle(row.label)
      return
    }
    onRespond(row.label)
  }

  const submitSelected = () => {
    if (isEmpty(selected)) {
      return
    }
    onRespond(selected.join(', '))
  }

  const submitOther = () => {
    if (isEmpty(trimmedOther)) {
      return
    }
    onRespond(trimmedOther)
  }

  const handleKeyDown = (event) => {
    // Esc ends the flow from anywhere in the panel (including the text box).
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (writingOther) {
      return
    }
    // 1–9 selects the matching row; Enter confirms a multi-select.
    if (event.key >= '1' && event.key <= '9') {
      const index = Number(event.key) - 1
      if (index < rows.length) {
        event.preventDefault()
        activateRow(rows[index])
      }
      return
    }
    if (event.key === 'Enter' && multiSelect && !isEmpty(selected)) {
      event.preventDefault()
      submitSelected()
    }
  }

  const handleOtherKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submitOther()
    }
  }

  return (
    <div
      ref={containerRef}
      role="listbox"
      aria-label={question}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="cowork-interactive-prompt"
      data-testid="cowork-interactive-prompt"
    >
      <div className="cowork-interactive-prompt__header">
        <div className="cowork-interactive-prompt__question">
          <Markdown renderer={kodiMarkdownRenderer}>{question}</Markdown>
        </div>
        <Button
          basic
          square
          size="small"
          aria-label="Dismiss"
          data-testid="cowork-interactive-close"
          onClick={onClose}
          icon={<IconX aria-hidden="true" />}
        />
      </div>

      <div className="cowork-interactive-prompt__options">
        {rows.map((row, index) => {
          const isSelected = multiSelect && !row.isOther && selected.includes(row.label)
          // Not Button's own `loading`: that hides the label and number under a
          // centered spinner. Keep the row readable and spin in the check slot.
          const isLoading = !row.isOther && row.label === loadingLabel
          return (
            <Button
              fullWidth
              active={isSelected}
              role="option"
              aria-selected={isSelected}
              key={row.isOther ? '__other' : row.label}
              className="cowork-interactive-option"
              disabled={disabled || isLoading}
              data-testid={`cowork-interactive-option-${index + 1}`}
              onClick={() => activateRow(row)}
            >
              <span className="cowork-interactive-option__num">{index + 1}</span>
              <span className="cowork-interactive-option__body">
                <span className="cowork-interactive-option__label">{stripLinks(row.label)}</span>
                {row.description && (
                  <span className="cowork-interactive-option__desc">
                    {stripLinks(row.description)}
                  </span>
                )}
              </span>
              {isSelected && (
                <IconCheck
                  className="cowork-interactive-option__check"
                  size={16}
                  aria-hidden="true"
                />
              )}
              {isLoading && (
                <Loader
                  size="small"
                  className="cowork-interactive-option__check"
                  data-testid={`cowork-interactive-option-${index + 1}-loader`}
                />
              )}
            </Button>
          )
        })}
      </div>

      {writingOther && (
        <div className="cowork-interactive-prompt__other">
          <TextArea
            className="cowork-interactive-prompt__textarea"
            data-testid="cowork-interactive-other-input"
            aria-label="Type your answer"
            placeholder="Type your answer…"
            rows={2}
            value={otherText}
            disabled={disabled}
            onChange={(event) => setOtherText(event.target.value)}
            onKeyDown={handleOtherKeyDown}
          />
          <div className="cowork-interactive-prompt__actions">
            <Button
              brand
              size="small"
              data-testid="cowork-interactive-other-send"
              disabled={disabled || isEmpty(trimmedOther)}
              onClick={submitOther}
            >
              Send
            </Button>
            <Button
              size="small"
              data-testid="cowork-interactive-cancel"
              onClick={() => {
                setWritingOther(false)
                setOtherText('')
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {multiSelect && !writingOther && (
        <div className="cowork-interactive-prompt__actions">
          <Button
            brand
            size="small"
            data-testid="cowork-interactive-send"
            disabled={disabled || isEmpty(selected)}
            onClick={submitSelected}
          >
            Send
          </Button>
        </div>
      )}
    </div>
  )
}
